import React, { FC, SVGProps } from 'react';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import { useTranslation } from 'react-i18next';
import { OpPlusDeliveryTruckIcon, OpPlusCoinIcon, OpPlusPiggyBankIcon } from 'components/Common/Icons/opplus';
import classNames from 'classnames';
import styles from './PlusBenefits.module.scss';

type PlusBenefitsSize = 'small' | 'large';

interface PlusBenefits {
  size?: PlusBenefitsSize;
  layout?: 'row' | 'column';
  title?: string;
}

interface BenefitItemProps {
  Icon: FC<SVGProps<SVGSVGElement>>;
  size: PlusBenefitsSize;
  layout: string;
  title: string;
  subtitle: string;
}

const BenefitItem: FC<BenefitItemProps> = ({ Icon, size, layout, title, subtitle }) => (
  <div
    className={classNames(styles.benefitsItem, {
      [styles.flexRow]: layout === 'row',
      [styles.flexColumn]: layout === 'column',
    })}
  >
    <Icon
      className={classNames({
        [styles.smallIcon]: size === 'small',
        [styles.largeIcon]: size === 'large',
      })}
    />
    <div
      className={classNames(styles.benefitsItemDescription, {
        [styles.flexRow]: layout === 'row',
        [styles.flexColumn]: layout === 'column',
        [styles.benefitsItemColumn]: layout === 'column',
      })}
    >
      <OpText block variant="body-large-bold">
        {title}
      </OpText>
      <OpText block variant="title-small-regular">
        {subtitle}
      </OpText>
    </div>
  </div>
);

const PlusBenefits: FC<PlusBenefits> = ({ size = 'small', layout = 'row', title }) => {
  const { t } = useTranslation();

  return (
    <div
      className={classNames({
        [styles.wrapperColumn]: layout === 'column',
      })}
    >
      {title && (
        <div className={styles.title}>
          <OpText variant="title-large-bold">{title}</OpText>
        </div>
      )}
      <div
        className={classNames(styles.benefits, {
          [styles.flexColumn]: layout === 'row',
          [styles.flexRow]: layout === 'column',
          [styles.containerColumn]: layout === 'column',
        })}
      >
        <BenefitItem
          Icon={OpPlusDeliveryTruckIcon}
          size={size}
          layout={layout}
          title={t('txt_plus_benefits_fast_delivery')}
          subtitle={t('txt_plus_benefits_every_order')}
        />
        <BenefitItem
          Icon={OpPlusCoinIcon}
          size={size}
          layout={layout}
          title={t('txt_plus_benefits_in_store_credits')}
          subtitle={t('txt_plus_benefits_all_orders')}
        />
        <BenefitItem
          Icon={OpPlusPiggyBankIcon}
          size={size}
          layout={layout}
          title={t('txt_plus_benefits_higher_discounts')}
          subtitle={t('txt_plus_benefits_save_per_order')}
        />
      </div>
    </div>
  );
};

export default PlusBenefits;
